import { Alert, Box, Button, ColumnLayout, SpaceBetween } from '@amzn/awsui-components-core';
import React, { useState } from 'react';
import { publicationLimit } from '../../api/UtilsApi';
import { reorderByKey } from '../../common/Arrays';
import { SortableList } from '../v2-common/SortableList';
import OtherPublicationInput from './OtherPublicationInput';
import {
  decreaseCountInCountMap,
  FormErrorType,
  OtherPublicationItem,
  updateCountMap,
} from './APGLUserProfileUtils';

// Stable across renders so SortableList's memoized id list stays valid.
const keyOfPublication = (pub: OtherPublicationItem) => String(pub.id);

enum CountMapType {
  TitleMap = 'TitleMap',
  UrlMap = 'UrlMap',
}

const createInitCountMap = (
  items: OtherPublicationItem[],
  type: CountMapType,
): Map<string, number> => {
  const initCountMap = new Map<string, number>();
  items.forEach((item) => {
    const { title, url } = item;
    const count =
      type === CountMapType.TitleMap
        ? initCountMap.has(title)
          ? // @ts-expect-error TS2532 - strict migration
            initCountMap.get(title) + 1
          : 1
        : initCountMap.has(url)
          ? // @ts-expect-error TS2532 - strict migration
            initCountMap.get(url) + 1
          : 1;
    type === CountMapType.TitleMap ? initCountMap.set(title, count) : initCountMap.set(url, count);
  });
  return initCountMap;
};

interface Props {
  items: OtherPublicationItem[];
  lastId: number; // Largest ID number that has been used for items in OtherPublicationList. It gets increased by 1 for each newly added item.
  handlePublicationsChanged: (pubs: OtherPublicationItem[]) => void;
  handleLastIdUpdated: (id: number) => void;
  handleCountMapUpdated: (titleMap: Map<string, number>, urlMap: Map<string, number>) => void; // for duplication check
  handleInputErrorUpdated: (inputError: Map<number, boolean>, type: FormErrorType) => void; // for input validation
}

export const OtherPublicationList: React.FunctionComponent<Props> = (props: Props) => {
  const {
    items,
    lastId,
    handlePublicationsChanged,
    handleLastIdUpdated,
    handleCountMapUpdated,
    handleInputErrorUpdated,
  } = props;

  const [pubs, setPubs] = useState<OtherPublicationItem[]>(items);
  const [latestId, setLatestId] = useState<number>(lastId);
  const [alert, setAlert] = useState<string>('');
  // title->count to track duplicated title
  const [titleMap, setTitleMap] = useState<Map<string, number>>(
    createInitCountMap(items, CountMapType.TitleMap),
  );
  // url->count to track duplicated url
  const [urlMap, setUrlMap] = useState<Map<string, number>>(
    createInitCountMap(items, CountMapType.UrlMap),
  );
  const [inputError, setInputError] = useState<Map<number, boolean>>(new Map<number, boolean>()); // id->has error?

  const onPublicationChanged = (pub: OtherPublicationItem) => {
    const currentPubs = [...pubs];
    const index = currentPubs.findIndex((p) => p.id === pub.id);
    // @ts-expect-error TS2532 - strict migration
    const curTitle = currentPubs[index].title;
    // @ts-expect-error TS2532 - strict migration
    const curUrl = currentPubs[index].url;
    const newTitle = pub.title;
    const newUrl = pub.url;
    if (curTitle !== newTitle) {
      updateCountMap(curTitle, newTitle, titleMap);
    }
    if (curUrl !== newUrl) {
      updateCountMap(curUrl, newUrl, urlMap);
    }
    currentPubs[index] = pub;
    setPubs(currentPubs);
    handlePublicationsChanged(currentPubs);
    setTitleMap(titleMap);
    setUrlMap(urlMap);
    handleCountMapUpdated(titleMap, urlMap);
  };

  const onDeletePublication = (id: number) => {
    setAlert('');
    const deletedPub = pubs.find((pub) => pub.id === id);
    // @ts-expect-error TS18048 - strict migration
    decreaseCountInCountMap(deletedPub.title, titleMap);
    // @ts-expect-error TS18048 - strict migration
    decreaseCountInCountMap(deletedPub.url, urlMap);
    const currentPubs = pubs.filter((pub) => pub.id !== id);
    setPubs(currentPubs);
    handlePublicationsChanged(currentPubs);
    handleCountMapUpdated(titleMap, urlMap);
  };

  const updateInputError = (id: number, titleError: string, urlError: string) => {
    if (titleError || urlError) {
      inputError.set(id, true);
    } else {
      inputError.set(id, false);
    }
    setInputError(inputError);
    handleInputErrorUpdated(inputError, FormErrorType.OtherPubInputError);
  };

  const onReorder = (orderedKeys: readonly string[]) => {
    const newPubs = reorderByKey(pubs, orderedKeys, keyOfPublication);
    setPubs(newPubs);
    handlePublicationsChanged(newPubs);
  };

  const publicationList = (
    <SortableList
      items={pubs}
      keyExtractor={keyOfPublication}
      onReorder={onReorder}
      item={(item, { setNodeRef, style }) => {
        // Flag duplicates so the input can show its inline alerts.
        const isTitleDuplicated = (titleMap.get(item.title) ?? 0) > 1;
        const isUrlDuplicated = (urlMap.get(item.url) ?? 0) > 1;
        return (
          <div ref={setNodeRef} style={style}>
            <OtherPublicationInput
              item={item}
              handlePublicationChanged={onPublicationChanged}
              handlePublicationDeleted={onDeletePublication}
              handleInputErrorUpdated={updateInputError}
              titleAlert={isTitleDuplicated ? 'This title already exists.' : ''}
              urlAlert={isUrlDuplicated ? 'This URL already exists.' : ''}
            />
          </div>
        );
      }}
    />
  );

  const onAddPublication = () => {
    if (pubs.length >= publicationLimit) {
      setAlert(`You can only add up to ${publicationLimit} publications.`);
    } else {
      setAlert('');
      const newId = latestId + 1;
      const newPub: OtherPublicationItem = { title: '', url: '', id: newId };
      const newPubs = [...pubs];
      newPubs.push(newPub);
      setPubs(newPubs);
      handlePublicationsChanged(newPubs);
      setLatestId(newId);
      handleLastIdUpdated(newId);
    }
  };

  const onDismissAlert = () => {
    setAlert('');
  };

  const alertSection = alert ? (
    <Alert onDismiss={onDismissAlert} dismissible dismissAriaLabel="Close alert" type="warning">
      {alert}
    </Alert>
  ) : null;

  const addButton = <Button onClick={onAddPublication}>Add publication</Button>;

  const publicationHeader = (
    <SpaceBetween direction="horizontal" size="xs">
      <Box variant="h3">Other content platforms or publications</Box>
      <Box variant="p" margin={{ top: 'xxxs' }}>{`(maximum ${publicationLimit})`}</Box>
    </SpaceBetween>
  );

  return (
    <SpaceBetween direction="vertical" size="s">
      {publicationHeader}
      <ColumnLayout columns={3}>
        <div>Title of publication</div>
        <div>URL</div>
      </ColumnLayout>
      <div>{publicationList}</div>
      {alertSection}
      {addButton}
    </SpaceBetween>
  );
};
